"use client";

import clsx from "clsx";
import { PhoneFrame } from "@/components/scene/PhoneFrame";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 0.3 / 0.4 — Brand-styled email mock inside a phone frame.
 * Replaces the prior WhatsApp framing — the buyer initiates contact via
 * email, the seller replies with the tokenised catalogue link.
 *
 *   variant="outbound" → 0.3 — Al Madar's draft email to Aurora.
 *   variant="inbound"  → 0.4 — Aurora's reply email landing on Al Madar.
 *
 * Not a real email integration — purely a brand-styled preview.
 */

type Variant = "outbound" | "inbound";

export function EmailMock({
  variant,
  className,
}: {
  variant: Variant;
  className?: string;
}) {
  const t = useTranslations("emailMock");
  const isInbound = variant === "inbound";

  const status    = isInbound ? t("inboundStatus")    : t("outboundStatus");
  const inboxFor  = isInbound ? t("inboundInbox")     : t("outboundInbox");
  const fromName  = isInbound ? t("inboundFromName")  : t("outboundFromName");
  const fromEmail = isInbound ? t("inboundFromEmail") : t("outboundFromEmail");
  const toName    = isInbound ? t("inboundToName")    : t("outboundToName");
  const toEmail   = isInbound ? t("inboundToEmail")   : t("outboundToEmail");
  const subject   = isInbound ? t("inboundSubject")   : t("outboundSubject");
  const body      = isInbound ? t("inboundBody")      : t("outboundBody");
  const sentLabel = isInbound ? t("inboundSent")      : t("outboundSent");
  const link      = isInbound ? t("inboundLink")      : undefined;

  return (
    <div className={clsx("flex flex-col items-center gap-3", className)}>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
        {isInbound ? t("chipInbound") : t("chipOutbound")}
      </p>
      <PhoneFrame status={status}>
        <div className="overflow-hidden rounded-lg border border-state-verified/20 bg-orbitra-ink/80">
          {/* Mail-app chrome */}
          <div className="flex items-center justify-between border-b border-state-verified/15 px-3 py-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {inboxFor}
            </p>
            <span className="rounded-full bg-state-verified/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-verified">
              {t("mailBadge")}
            </span>
          </div>

          {/* From / To */}
          <div className="space-y-1.5 px-3 pt-3 text-[10px]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono uppercase tracking-[0.18em] text-orbitra-muted">{t("fieldFrom")}</span>
              <span className="truncate text-orbitra-paper">
                <span className="font-semibold">{fromName}</span>{" "}
                <span className="text-orbitra-muted">&lt;{fromEmail}&gt;</span>
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono uppercase tracking-[0.18em] text-orbitra-muted">{t("fieldTo")}</span>
              <span className="truncate text-orbitra-paper">
                <span className="font-semibold">{toName}</span>{" "}
                <span className="text-orbitra-muted">&lt;{toEmail}&gt;</span>
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono uppercase tracking-[0.18em] text-orbitra-muted">{t("fieldSubject")}</span>
              <span className="truncate font-semibold text-orbitra-paper">{subject}</span>
            </div>
          </div>

          {/* Body */}
          <div className="border-t border-state-verified/10 px-3 pb-2 pt-3 text-[11px] leading-snug text-orbitra-paper">
            <pre className="whitespace-pre-wrap break-words font-sans">{body}</pre>
            {link && (
              <a className="mt-3 block break-all rounded-md bg-orbitra-ink/70 px-2 py-1 font-mono text-[10px] text-state-verified">
                {link}
              </a>
            )}
            <p className={clsx("mt-2 text-[9px] text-orbitra-muted", isInbound ? "text-start" : "text-end")}>
              {sentLabel}
            </p>
          </div>

          {/* Footer chip */}
          <div className="flex items-center gap-2 border-t border-state-verified/15 px-3 py-2">
            {isInbound ? (
              <span className="inline-flex items-center gap-1 rounded-md border border-state-verified/40 bg-state-verified/15 px-2 py-1 text-[10px] font-semibold text-state-verified">
                {t("tapLink")} <span>↗</span>
              </span>
            ) : (
              <>
                <div className="flex-1 rounded-full bg-orbitra-ink/60 px-3 py-1.5 text-[11px] text-orbitra-teal-soft">
                  {t("replyForwardArchive")}
                </div>
                <span className="grid h-8 w-8 place-items-center rounded-full bg-orbitra-muted/30 font-display text-sm font-semibold text-orbitra-muted">
                  ↑
                </span>
              </>
            )}
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}
